import React, { useEffect } from "react";
import { createTheme, ThemeProvider, Box } from "@mui/material";
import {
    BrowserRouter as Router,
    Routes,
    Route,
    Navigate,
} from "react-router-dom";
import BlastTable from "./components/Blasts/BlastTable";
import UserTable from "./components/Users/UserTable";
import Navbar from "./components/Navigation/Navbar";
import Header from "./components/Header/Header";
import { styled } from "@mui/material/styles";
import { useAccount } from './contexts/AccountContext';
import SentBlasts from './components/Blasts/SentBlasts';
import LandingPage from "./components/LandingPage/LandingPage";
import PrivacyPolicy from './components/LandingPage/PrivacyPolicy';
import TermsAndConditions from './components/LandingPage/TermsAndConditions';
import AboutUs from './components/LandingPage/AboutUs';
import FAQ from './components/LandingPage/FAQ';
import locations from './components/LandingPage/Locations';
import ErrorBoundary from './components/ErrorBoundary';
import SignUpForm from './forms/SignUpForm';
import AdminPortal from './components/Admin/AdminPortal';
import { AccountProvider } from './contexts/AccountContext';
import { FirebaseProvider } from './contexts/FirebaseContext';
import AuthCallback from './components/oauth/AuthCallback';
import { initGA, logPageView } from './services/analytics';
import Locations from "./components/LandingPage/Locations";
import PatronInfo from "./components/Users/PatronInfo";

// Styled components
const MainContainer = styled("div")({});

const ContentContainer = styled("div")({});

// Theme remains the same
const theme = createTheme({});

function App() {
    useEffect(() => {
        // Initialize GA
        initGA();
        // Log initial page view
        logPageView();
        
        // Log page views on route changes
        const handleRouteChange = () => {
            logPageView();
        };

        window.addEventListener('popstate', handleRouteChange);
        return () => window.removeEventListener('popstate', handleRouteChange);
    }, []);

    return (
        <ErrorBoundary>
            <AccountProvider>
                <ThemeProvider theme={theme}>
                    <FirebaseProvider>
                        <Router>
                            <AppRoutes />
                        </Router>
                    </FirebaseProvider>
                </ThemeProvider>
            </AccountProvider>
        </ErrorBoundary>
    );
}

// Separate component for routes to use hooks
function AppRoutes() {
    const { userData, setUserData } = useAccount();
    const adminEmails = process.env.REACT_APP_ADMIN_EMAILS.split(',');
    const isAdmin = userData?.email && adminEmails.includes(userData.email);

    return (  
        <Routes>
            {/* Public Routes */}
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/faq" element={<FAQ />} />
            <Route path="/locations" element={<Locations />} />

            
            {/* Auth Callback Route */}
            <Route path="/auth/callback" element={<AuthCallback />} />
            
            {/* Default Route - Always LandingPage when unauthenticated */}
            <Route 
                path="/" 
                element={!userData ? <LandingPage /> : (
                    <Navigate to="/blasts" replace />
                )} 
            />

            {/* Authenticated Routes */}
            <Route 
                path="/blasts" 
                element={userData ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box
                                sx={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "12px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <BlastTable />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/" replace />
                )} 
            />

            {/* Users Route */}
            <Route 
                path="/users" 
                element={userData ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box
                                sx={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "12px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <UserTable userData={userData} />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/" replace />
                )} 
            />

            {/* Sent Blasts Route */}
            <Route 
                path="/sent-blasts" 
                element={userData ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box
                                sx={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "12px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <SentBlasts currentAccountId={userData.accountId} />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/" replace />
                )} 
            />

            {/* Patron Info Route */}
            <Route 
                path="/patron-info" 
                element={userData ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box
                                sx={{
                                    backgroundColor: "#ffffff",
                                    borderRadius: "12px",
                                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                                }}
                            >
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <PatronInfo />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/" replace />
                )} 
            />

            {/* Bar Management Route */}
            <Route 
                path="/bar-management" 
                element={userData?.isAdmin ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box sx={{
                                backgroundColor: "#ffffff",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}>
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <AdminPortal />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/" replace />
                )} 
            />

            {/* Join Route */}
            <Route 
                path="/join/:barSlug" 
                element={
                    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5' }}>
                        <SignUpForm />
                    </Box>
                } 
            />

            {/* Admin Route */}
            <Route 
                path="/admin" 
                element={isAdmin ? (
                    <MainContainer>
                        <Header userData={userData} setUserData={setUserData} />
                        <ContentContainer className="px-8 py-4">
                            <Box sx={{
                                backgroundColor: "#ffffff",
                                borderRadius: "12px",
                                boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                            }}>
                                <Navbar handleLogout={() => setUserData(null)} />
                                <Box sx={{ padding: "1 1rem" }}>
                                    <AdminPortal />
                                </Box>
                            </Box>
                        </ContentContainer>
                    </MainContainer>
                ) : (
                    <Navigate to="/blasts" replace />
                )} 
            />

         

            {/* Catch-all route to redirect to landing page */}
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

export default App;